import { Button } from '@/components/common/Button';
import { DialogWrapper } from '@/components/common/DialogWrapper';
import { Input } from '@/components/common/Input';
import type { StockTableRow } from '@/domain/Stock';
import { useEffect, useState } from 'react';

export interface AddStockFormData {
  amount: number;
  expire_date: string;
  lot_number: string;
}

interface StockFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stockRow: StockTableRow | null;
  onSubmit: (id: number, data: AddStockFormData) => void;
  isPending?: boolean;
}

const getTomorrowDate = (): string => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split('T')[0];
};

export function StockFormDialog({
  open,
  onOpenChange,
  stockRow,
  onSubmit,
  isPending,
}: StockFormDialogProps) {
  const [amount, setAmount] = useState('');
  const [expireDate, setExpireDate] = useState('');
  const [lotNumber, setLotNumber] = useState('');

  useEffect(() => {
    if (open) {
      setAmount('');
      setExpireDate('');
      setLotNumber('');
    }
  }, [open]);

  const isValid =
    stockRow?.stockId &&
    amount &&
    Number(amount) >= 1 &&
    expireDate &&
    lotNumber.trim().length > 0 &&
    lotNumber.trim().length <= 20;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (stockRow && isValid) {
      onSubmit(stockRow.stockId, {
        amount: Number(amount),
        expire_date: expireDate,
        lot_number: lotNumber.trim(),
      });
    }
  };

  return (
    <DialogWrapper
      open={open}
      onOpenChange={onOpenChange}
      title="Add stock batch"
      maxWidth="md"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Input
          id="name"
          label="Name"
          value={stockRow?.supplementName || ''}
          disabled
          className="bg-white"
        />

        <Input
          id="strength"
          label="Item Strength"
          value={stockRow?.strength || ''}
          disabled
          className="bg-white"
        />

        <Input
          id="amount"
          label="Quantity"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="bg-white"
          min={1}
        />

        <Input
          id="expire_date"
          label="Expiry date"
          type="date"
          value={expireDate}
          onChange={(e) => setExpireDate(e.target.value)}
          className="bg-white"
          min={getTomorrowDate()}
        />

        <Input
          id="lot_number"
          label="LOT number"
          value={lotNumber}
          onChange={(e) => setLotNumber(e.target.value)}
          className="bg-white"
          maxLength={20}
        />

        <Button
          type="submit"
          variant="primary"
          size="md"
          disabled={isPending || !isValid}
          className="mt-2 w-48"
        >
          {isPending ? 'Saving...' : 'Save changes'}
        </Button>
      </form>
    </DialogWrapper>
  );
}
